/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';

import { FormControl, FormHelperText, InputLabel, OutlinedInput } from '@mui/material';

import { hasOrganizationPermission, KubernetesIdentifier } from 'shared';

import { useEventCallback } from '../../../components/hooks/useEventCallback';
import { ThumbnailSelector, ThumbnailSelectorType } from '../../../components/ThumbnailSelector/ThumbnailSelector';
import { Namespaces } from '../../../i18n';
import { RootState } from '../../../store';
import { useFormStyles } from '../../../styles/form';
import { ThumbnailOption } from '../../../types/thumbnail/ThumbnailOption';
import { readFile } from '../../../utils/canvasUtils';
import { PickerModal } from './modal/PickerModal';
import { TCommonCreateForm, TExtendedFormKeys, TExtendedFormValues } from './types';

const mapState = (state: RootState) => ({
  organizations: state.organizations,
  auth: state.auth,
});

const connector = connect(mapState);
type PropsFromRedux = Partial<ConnectedProps<typeof connector>>;

const CommonCreateFormComponent = memo<TCommonCreateForm & PropsFromRedux>(
  ({
    type,
    formProps,
    hasFieldApiError,
    getFieldApiError,
    organizations,
    auth,
    pickedState,
    setPickedState,
    isFetchingOrganizations,
  }) => {
    const {
      values: { thumbnailsOptions },
      values,
      setFieldValue,
      errors,
      touched,
      handleBlur,
      handleChange,
      setValues,
    } = formProps;

    const { t } = useTranslation([Namespaces.CreateProject, Namespaces.Common, Namespaces.Entities]);

    const formClasses = useFormStyles();

    const hasError = (name: TExtendedFormKeys) =>
      !!(touched as TExtendedFormValues)[name] && !!((errors as TExtendedFormValues)[name] || hasFieldApiError(name));

    const getError = (name: TExtendedFormKeys): string | undefined => {
      const error = (errors as TExtendedFormValues)[name] || getFieldApiError(name);
      const translationNamespace = Namespaces.CreateProject;
      const translationKey = `validation.${name}.${error}`;
      return error
        ? t(`${translationNamespace}:${translationKey}`, {
            minLength: KubernetesIdentifier.MinLength,
            maxLength: KubernetesIdentifier.MaxLength,
          })
        : undefined;
    };

    const handleSetThumbnail = (value: ThumbnailOption) => setFieldValue('thumbnail', value);

    const handleImageUpload = useEventCallback(async (thumbnail: Blob) => {
      const newThumbnailOption: ThumbnailOption = { type: 'base64', base64: await readFile(thumbnail) };
      void setValues({
        ...values,
        thumbnailsOptions: [...thumbnailsOptions, newThumbnailOption],
        thumbnail: newThumbnailOption,
      });
    });

    useEffect(() => {
      const setDefaultThumbnail = async () => {
        if (values.thumbnail === null && thumbnailsOptions[0]) {
          await setFieldValue('thumbnail', thumbnailsOptions[0]);
        }
      };

      void setDefaultThumbnail();
    }, [setFieldValue, thumbnailsOptions, values.thumbnail]);

    const organizationsWithPermission = useMemo(
      () =>
        organizations?.options.filter(organization =>
          hasOrganizationPermission(
            auth?.user?.organizations.find(({ id }) => organization.id === id)?.roles || [],
            'create_project',
          ),
        ),
      [organizations, auth],
    );

    useEffect(() => {
      if (values.organizationId) {
        const currentOrganization = organizationsWithPermission?.find(({ id }) => id === values.organizationId) || null;
        void setFieldValue('organization', currentOrganization);
      }
    }, [organizationsWithPermission, setFieldValue, values]);

    const getTranslationKey = (key: string) => `${Namespaces.CreateProject}:${key}`;

    return (
      <>
        {!!thumbnailsOptions.length && (
          <FormControl className={cx(formClasses.formControl, formClasses.widthFull)}>
            <ThumbnailSelector
              onItemClick={handleSetThumbnail}
              options={thumbnailsOptions}
              type={ThumbnailSelectorType.project}
              value={values.thumbnail}
              allowUploadImage
              onImageUpload={handleImageUpload}
            />
            {hasError('thumbnail') && <FormHelperText>{getError('thumbnail')}</FormHelperText>}
          </FormControl>
        )}
        <FormControl className={cx(formClasses.formControl, formClasses.widthMedium)} error={hasError('organization')}>
          <PickerModal
            type={type}
            values={values as TExtendedFormValues}
            setValues={setValues}
            pickedState={pickedState}
            setPickedState={setPickedState}
            organizations={organizationsWithPermission}
            isFetchingOrganizations={isFetchingOrganizations}
          />
          {hasError('organization') && <FormHelperText>{getError('organization')}</FormHelperText>}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthDefault)}
          error={hasError('name')}
          variant="outlined"
        >
          <InputLabel htmlFor="name">{t(getTranslationKey('input_labels.name'))}</InputLabel>
          <OutlinedInput
            id="name"
            name="name"
            value={values.name}
            onChange={handleChange}
            label={t(getTranslationKey('input_labels.name'))}
            onBlur={handleBlur}
            autoFocus
            data-cy="create-form-name-field"
          />
          {hasError('name') && <FormHelperText data-cy="name-error">{getError('name')}</FormHelperText>}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthMedium)}
          error={hasError('description')}
          variant="outlined"
        >
          <InputLabel htmlFor="description">{t(getTranslationKey('input_labels.description'))}</InputLabel>
          <OutlinedInput
            id="description"
            name="description"
            value={values.description}
            onChange={handleChange}
            label={t(getTranslationKey('input_labels.description'))}
            fullWidth
            multiline
            onBlur={handleBlur}
            data-cy="create-form-description-field"
          />
          {hasError('description') && <FormHelperText>{getError('description')}</FormHelperText>}
        </FormControl>
      </>
    );
  },
);

export const CommonCreateForm = connector(CommonCreateFormComponent);
